import {
  type ClubRole,
  getMergedThresholds,
  getRankFromElo,
  getRankLevel,
  getTeamNames,
} from "@rivaled/shared";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  Ban,
  ChevronDown,
  ExternalLink,
  FileText,
  Gavel,
  ImageIcon,
  Link2,
  ListChecks,
  Paperclip,
  PlayCircle,
  RefreshCcw,
  Shield,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Avatar,
  Badge,
  Button,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RankIcon,
  SectionCard,
  Select,
  Switch,
  Textarea,
  Tooltip,
} from "../../../../../components/ui";
import { getApiBaseUrl } from "../../../../../lib/apiBaseUrl";
import { useSession } from "../../../../../lib/auth-client";
import { canClubRolePermission } from "../../../../../lib/permissions";
import { trpc, type RouterOutputs } from "../../../../../lib/trpc";
import { uploadFile } from "../../../../../lib/uploadFile";
import { cn } from "../../../../../lib/utils";
import { getTeamColor } from "../../../../../match-flow/lib/team-colors";

type ResolutionValue = "upheld" | "overturned" | "cancelled";
type BatchResolutionValue = "cancelled";
type PendingResolution = {
  resolution: ResolutionValue;
  correctedWinner?: number;
};
type PendingBatchResolution = {
  resolution: BatchResolutionValue;
};
type StatusFilterValue = "all_unresolved" | "open" | "under_review" | "resolved";
type SortValue = "oldest" | "newest";
type ClaimFilterValue = "all" | "claimed_by_me" | "claimed_by_others" | "unclaimed";
type ClaimerFilterValue = "all" | "me" | "unclaimed" | `user:${string}`;
type DisputeListItem = RouterOutputs["dispute"]["listPending"]["items"][number];
type DisputeDetail = NonNullable<RouterOutputs["dispute"]["getDetail"]>;

const statusFilterOptions = [
  { value: "all_unresolved", label: "All Unresolved" },
  { value: "open", label: "Open" },
  { value: "under_review", label: "Under Review" },
  { value: "resolved", label: "Resolved" },
] as const;

const sortOptions = [
  { value: "oldest", label: "Oldest First" },
  { value: "newest", label: "Newest First" },
] as const;
const statusBadgeColor: Record<string, "gray" | "blue" | "yellow" | "green" | "red"> = {
  open: "yellow",
  under_review: "blue",
  resolved: "green",
};
const resolutionBadgeColor: Record<string, "gray" | "blue" | "yellow" | "green" | "red"> = {
  upheld: "green",
  overturned: "blue",
  cancelled: "red",
};

const apiBaseUrl = getApiBaseUrl();

export const Route = createFileRoute("/app/clubs/$slug/admin/disputes")({
  validateSearch: (search: Record<string, unknown>) => {
    const rawStatus = search.status;
    const rawSort = search.sort;
    const rawClaim = search.claim;
    const rawClaimer = search.claimer;
    const rawDispute = search.dispute;

    const status: StatusFilterValue =
      rawStatus === "open" ||
      rawStatus === "under_review" ||
      rawStatus === "resolved" ||
      rawStatus === "all_unresolved"
        ? rawStatus
        : "all_unresolved";

    const sort: SortValue = rawSort === "newest" || rawSort === "oldest" ? rawSort : "oldest";
    const claim: ClaimFilterValue =
      rawClaim === "claimed_by_me" ||
      rawClaim === "claimed_by_others" ||
      rawClaim === "unclaimed" ||
      rawClaim === "all"
        ? rawClaim
        : "all";
    const claimer: ClaimerFilterValue =
      rawClaimer === "all" ||
      rawClaimer === "me" ||
      rawClaimer === "unclaimed" ||
      (typeof rawClaimer === "string" && rawClaimer.startsWith("user:"))
        ? (rawClaimer as ClaimerFilterValue)
        : "all";
    const dispute =
      typeof rawDispute === "string" && rawDispute.length > 0 ? rawDispute : undefined;

    return { status, sort, claim, claimer, dispute };
  },
  component: ClubAdminDisputesPage,
});

function ClubAdminDisputesPage() {
  const { slug } = Route.useParams();
  const { status, sort, claim, claimer, dispute } = Route.useSearch();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const { data: club } = trpc.club.get.useQuery({ slug });
  const { data: myClubs } = trpc.club.getMyClubs.useQuery();
  const membership = myClubs?.find((c) => c.slug === slug);
  const role = membership?.role as ClubRole | undefined;

  const canModerate = canClubRolePermission(role, {
    resource: "dispute",
    actions: ["view"],
  });
  const canResolve = canClubRolePermission(role, {
    resource: "dispute",
    actions: ["resolve"],
  });
  const { data: session } = useSession();

  const statusFilter: StatusFilterValue = status;
  const sortOrder: SortValue = sort;
  const claimFilter: ClaimFilterValue = claim;
  const claimerFilter: ClaimerFilterValue = claimer;
  const listClaimFilter: "all" | "claimed" | "unclaimed" =
    claimerFilter === "unclaimed" || claimFilter === "unclaimed"
      ? "unclaimed"
      : claimerFilter === "me" ||
          (typeof claimerFilter === "string" && claimerFilter.startsWith("user:")) ||
          claimFilter === "claimed_by_me" ||
          claimFilter === "claimed_by_others"
        ? "claimed"
        : "all";
  const listClaimedById: string | undefined =
    claimerFilter === "me"
      ? session?.user?.id
      : claimerFilter.startsWith("user:")
        ? claimerFilter.slice(5)
        : claimFilter === "claimed_by_me"
          ? session?.user?.id
          : undefined;
  const selectedDisputeId = dispute ?? null;
  const [adminNotes, setAdminNotes] = useState("");
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([]);
  const [pendingBatchResolution, setPendingBatchResolution] =
    useState<PendingBatchResolution | null>(null);
  const [batchAdminNotes, setBatchAdminNotes] = useState("");

  const pendingQuery = trpc.dispute.listPending.useQuery(
    {
      clubId: club?.id ?? "",
      limit: 50,
      status: statusFilter,
      sort: sortOrder,
      claim: listClaimFilter,
      claimedById: listClaimedById,
    },
    { enabled: !!club?.id && canModerate },
  );

  const rawDisputes = pendingQuery.data?.items ?? [];
  const claimerFilterOptions = useMemo(() => {
    const options = [
      { value: "all", label: "Any Claimer" },
      { value: "me", label: "Claimed by Me" },
      { value: "unclaimed", label: "Unclaimed Only" },
    ];
    const byUserId = new Map<string, string>();
    for (const item of rawDisputes) {
      if (!item.claimedById) continue;
      if (item.claimedById === session?.user?.id) continue;
      const fallback = `Admin ${item.claimedById.slice(-8)}`;
      byUserId.set(item.claimedById, item.claimedByUsername || fallback);
    }

    const adminOptions = Array.from(byUserId.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([userId, username]) => ({
        value: `user:${userId}`,
        label: `Claimed by ${username}`,
      }));

    return [...options, ...adminOptions];
  }, [rawDisputes, session?.user?.id]);
  const disputes = useMemo(() => {
    const byClaimStatus = rawDisputes.filter((item) => {
      const claimedById = item.claimedById ?? null;
      if (claimFilter === "all") return true;
      if (claimFilter === "unclaimed") return claimedById === null;
      if (claimFilter === "claimed_by_me")
        return claimedById !== null && claimedById === session?.user?.id;
      return claimedById !== null && claimedById !== session?.user?.id;
    });

    return byClaimStatus.filter((item) => {
      const claimedById = item.claimedById ?? null;
      if (claimerFilter === "all") return true;
      if (claimerFilter === "me") return claimedById !== null && claimedById === session?.user?.id;
      if (claimerFilter === "unclaimed") return claimedById === null;
      if (claimerFilter.startsWith("user:")) {
        return claimedById !== null && claimedById === claimerFilter.slice(5);
      }
      return true;
    });
  }, [rawDisputes, claimFilter, claimerFilter, session?.user?.id]);
  const selectableDisputeIds = useMemo(
    () =>
      new Set(
        disputes
          .filter((item) => item.status !== "resolved" && item.category === "technical")
          .map((item) => item.id),
      ),
    [disputes],
  );
  const visibleSelectableDisputeIds = useMemo(
    () => disputes.filter((item) => selectableDisputeIds.has(item.id)).map((item) => item.id),
    [disputes, selectableDisputeIds],
  );
  const visibleSelectableCount = visibleSelectableDisputeIds.length;
  const canUseBatchMode = canResolve && statusFilter !== "resolved" && visibleSelectableCount > 0;
  const selectedBatchCount = selectedBatchIds.length;
  const selectedBatchDisputes = useMemo(
    () => disputes.filter((item) => selectedBatchIds.includes(item.id)),
    [disputes, selectedBatchIds],
  );
  const canBatchResolve = canUseBatchMode && selectedBatchCount > 0;
  const displayedDisputes = useMemo(
    () =>
      isBatchMode
        ? disputes.filter((item) => item.status !== "resolved" && item.category === "technical")
        : disputes,
    [disputes, isBatchMode],
  );

  const updateSearch = useCallback(
    (
      next: Partial<{
        status: StatusFilterValue;
        sort: SortValue;
        claim: ClaimFilterValue;
        claimer: ClaimerFilterValue;
        dispute?: string | null;
      }>,
    ) => {
      void navigate({
        to: "/app/clubs/$slug/admin/disputes",
        params: { slug },
        search: {
          status: next.status ?? statusFilter,
          sort: next.sort ?? sortOrder,
          claim: next.claim ?? claimFilter,
          claimer: next.claimer ?? claimerFilter,
          dispute:
            next.dispute === undefined
              ? (selectedDisputeId ?? undefined)
              : (next.dispute ?? undefined),
        },
        replace: true,
      });
    },
    [navigate, slug, statusFilter, sortOrder, claimFilter, claimerFilter, selectedDisputeId],
  );

  useEffect(() => {
    if (disputes.length === 0) {
      if (selectedDisputeId) {
        updateSearch({ dispute: null });
      }
      return;
    }

    const isSelectedVisible = disputes.some((item) => item.id === selectedDisputeId);
    if (!selectedDisputeId || !isSelectedVisible) {
      updateSearch({ dispute: disputes[0]?.id });
    }
  }, [disputes, selectedDisputeId, updateSearch]);

  useEffect(() => {
    setSelectedBatchIds((current) => {
      const next = current.filter((id) => selectableDisputeIds.has(id));
      if (next.length === current.length && next.every((id, index) => id === current[index])) {
        return current;
      }
      return next;
    });
  }, [selectableDisputeIds]);

  useEffect(() => {
    if (canUseBatchMode) return;
    setIsBatchMode(false);
    setSelectedBatchIds([]);
  }, [canUseBatchMode]);

  const detailQuery = trpc.dispute.getDetail.useQuery(
    { disputeId: selectedDisputeId ?? "" },
    { enabled: !!selectedDisputeId && canModerate },
  );

  const detailDisputeId = detailQuery.data?.dispute.id ?? null;
  const detailAdminNotes = detailQuery.data?.dispute.adminNotes ?? "";

  useEffect(() => {
    if (!selectedDisputeId || detailDisputeId !== selectedDisputeId) {
      setAdminNotes("");
      return;
    }
    setAdminNotes(detailAdminNotes);
  }, [selectedDisputeId, detailDisputeId, detailAdminNotes]);

  const selectedDispute = useMemo(
    () => disputes.find((item) => item.id === selectedDisputeId) ?? null,
    [disputes, selectedDisputeId],
  );

  const resolveMutation = trpc.dispute.resolve.useMutation({
    onSuccess: async () => {
      toast.success("Dispute resolved");
      await Promise.all([
        utils.dispute.listPending.invalidate({ clubId: club?.id ?? "" }),
        utils.dispute.getDetail.invalidate(),
      ]);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to resolve dispute");
    },
  });
  const resolveBatchMutation = trpc.dispute.resolveBatch.useMutation({
    onSuccess: async (result) => {
      if (result.resolvedCount > 0) {
        toast.success(
          `Resolved ${result.resolvedCount} dispute${result.resolvedCount === 1 ? "" : "s"}`,
        );
      }
      if (result.failedCount > 0) {
        const previewLimit = 5;
        const previewFailures = result.failed
          .slice(0, previewLimit)
          .map((failure) => `#${failure.disputeId.slice(-8)}: ${failure.message}`);
        const remainingFailures = result.failed.length - previewFailures.length;
        const description =
          remainingFailures > 0
            ? `${previewFailures.join("\n")}\n+${remainingFailures} more`
            : previewFailures.join("\n");
        toast.error(
          `${result.failedCount} dispute${result.failedCount === 1 ? "" : "s"} failed to resolve`,
          description ? { description } : undefined,
        );
      }
      setSelectedBatchIds((current) =>
        result.resolvedCount > 0
          ? current.filter((id) => !result.resolvedDisputeIds.includes(id))
          : current,
      );
      if (result.resolvedCount > 0 || result.failedCount === 0) {
        setPendingBatchResolution(null);
        setBatchAdminNotes("");
      }
      await Promise.all([
        utils.dispute.listPending.invalidate({ clubId: club?.id ?? "" }),
        utils.dispute.getDetail.invalidate(),
      ]);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to batch resolve disputes");
    },
  });
  const claimMutation = trpc.dispute.claim.useMutation({
    onSuccess: async () => {
      toast.success("Dispute claimed");
      await Promise.all([
        utils.dispute.listPending.invalidate({ clubId: club?.id ?? "" }),
        utils.dispute.getDetail.invalidate(),
      ]);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to claim dispute");
    },
  });
  const unclaimMutation = trpc.dispute.unclaim.useMutation({
    onSuccess: async () => {
      toast.success("Claim released");
      await Promise.all([
        utils.dispute.listPending.invalidate({ clubId: club?.id ?? "" }),
        utils.dispute.getDetail.invalidate(),
      ]);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to release claim");
    },
  });

  const handleResolve = (
    resolution: ResolutionValue,
    correctedWinner?: number,
    correctionReason?: string,
  ) => {
    if (!club || !selectedDisputeId) return;

    resolveMutation.mutate({
      disputeId: selectedDisputeId,
      clubId: club.id,
      resolution,
      adminNotes: adminNotes.trim() ? adminNotes.trim() : undefined,
      correctedWinner,
      correctionReason: correctionReason?.trim() ? correctionReason.trim() : undefined,
    });
  };
  const handleClaim = () => {
    if (!club || !selectedDisputeId) return;
    claimMutation.mutate({
      disputeId: selectedDisputeId,
      clubId: club.id,
    });
  };
  const handleUnclaim = () => {
    if (!club || !selectedDisputeId) return;
    unclaimMutation.mutate({
      disputeId: selectedDisputeId,
      clubId: club.id,
    });
  };
  const toggleBatchSelection = (disputeId: string) => {
    if (!selectableDisputeIds.has(disputeId)) return;
    setSelectedBatchIds((current) =>
      current.includes(disputeId)
        ? current.filter((id) => id !== disputeId)
        : [...current, disputeId],
    );
  };
  const toggleSelectAllVisible = () => {
    if (visibleSelectableDisputeIds.length === 0) {
      return;
    }
    setSelectedBatchIds((current) => {
      const allSelected = visibleSelectableDisputeIds.every((id) => current.includes(id));
      if (allSelected) {
        return current.filter((id) => !visibleSelectableDisputeIds.includes(id));
      }
      const merged = new Set([...current, ...visibleSelectableDisputeIds]);
      return Array.from(merged);
    });
  };
  const openBatchResolutionModal = () => {
    if (!canBatchResolve) return;
    setPendingBatchResolution({ resolution: "cancelled" });
  };
  const closeBatchResolutionModal = () => {
    if (resolveBatchMutation.isPending) return;
    setPendingBatchResolution(null);
    setBatchAdminNotes("");
  };
  const confirmBatchResolution = () => {
    if (!club || !pendingBatchResolution || selectedBatchIds.length === 0) return;
    if (batchAdminNotes.trim().length < 10) return;
    resolveBatchMutation.mutate({
      clubId: club.id,
      disputeIds: selectedBatchIds,
      resolution: "cancelled",
      adminNotes: batchAdminNotes.trim(),
    });
  };
  const allVisibleSelected =
    visibleSelectableCount > 0 &&
    visibleSelectableDisputeIds.every((itemId) => selectedBatchIds.includes(itemId));

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <SectionCard
        className="h-full lg:col-span-1"
        bodyClassName="flex h-full min-h-0 flex-col"
        header={
          <div className="flex items-center justify-between gap-2 px-4 py-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-bold uppercase tracking-wider text-white">Queue</h2>
              <Badge variant="subtle" color={disputes.length > 0 ? "yellow" : "gray"}>
                {disputes.length}
              </Badge>
            </div>
            <div className="flex items-center gap-1">
              {canUseBatchMode && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => {
                    setIsBatchMode((current) => {
                      if (current) {
                        setSelectedBatchIds([]);
                      }
                      return !current;
                    });
                  }}
                  className={cn(
                    "transition-colors",
                    isBatchMode
                      ? "text-brand-400 bg-brand-500/15"
                      : "text-surface-400 hover:text-brand-400 hover:bg-surface-700/40",
                  )}
                  title="Bulk void mode"
                >
                  <ListChecks className="size-3.5" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => pendingQuery.refetch()}
                className="text-surface-400 hover:text-brand-400 hover:bg-surface-700/40 transition-colors"
                title="Refresh queue"
              >
                <RefreshCcw
                  className={cn(
                    "size-3.5",
                    (pendingQuery.isFetching || pendingQuery.isLoading) && "animate-spin",
                  )}
                />
              </Button>
            </div>
          </div>
        }
      >
        <div className="flex h-full min-h-0 flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            <Select
              options={[...statusFilterOptions]}
              value={statusFilter}
              onChange={(value) => {
                updateSearch({ status: value as StatusFilterValue });
              }}
            />
            <Select
              options={[...sortOptions]}
              value={sortOrder}
              onChange={(value) => {
                updateSearch({ sort: value as SortValue });
              }}
            />
            <div className="col-span-2">
              <Select
                options={claimerFilterOptions}
                value={claimerFilter}
                onChange={(value) => {
                  updateSearch({ claimer: value as ClaimerFilterValue });
                }}
              />
            </div>
          </div>

          {isBatchMode && (
            <div className="rounded-lg border border-brand-500/20 bg-brand-500/5 px-2.5 py-2 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-surface-400">
                  {selectedBatchCount > 0 ? (
                    <>
                      <span className="font-semibold text-brand-400">{selectedBatchCount}</span>
                      {" of "}
                      {displayedDisputes.length} selected
                    </>
                  ) : (
                    <>
                      Showing {displayedDisputes.length} technical dispute
                      {displayedDisputes.length === 1 ? "" : "s"}
                    </>
                  )}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsBatchMode(false);
                    setSelectedBatchIds([]);
                  }}
                  className="h-auto px-0 text-2xs font-medium text-surface-500 hover:text-surface-300 transition-colors shrink-0"
                >
                  Cancel
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSelectAllVisible}
                  className="flex-1"
                >
                  {allVisibleSelected ? "Deselect All" : "Select All"}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={!canBatchResolve}
                  onClick={openBatchResolutionModal}
                  className="flex-1"
                >
                  Void Selected
                </Button>
              </div>
            </div>
          )}

          {pendingQuery.isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 rounded-lg bg-surface-800/40 animate-pulse" />
              ))}
            </div>
          ) : pendingQuery.error ? (
            <div className="rounded-lg border border-error-500/30 bg-error-500/10 p-3 text-sm text-error-300">
              {pendingQuery.error.message}
            </div>
          ) : displayedDisputes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="flex items-center justify-center size-10 rounded-xl bg-surface-800 border border-surface-700/50 mb-3">
                {isBatchMode ? (
                  <ListChecks className="size-5 text-surface-500" />
                ) : (
                  <Gavel className="size-5 text-surface-500" />
                )}
              </div>
              {isBatchMode ? (
                <>
                  <p className="text-sm font-medium text-surface-300">No technical disputes</p>
                  <p className="mt-1 text-xs text-surface-500">
                    Bulk void only applies to technical disputes. None match your current filters.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-surface-300">No disputes found</p>
                  <p className="mt-1 text-xs text-surface-500">
                    {statusFilter === "all_unresolved"
                      ? "All clear — no unresolved disputes."
                      : `No ${formatDisputeStatus(statusFilter).toLowerCase()} disputes.`}
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="flex-1 min-h-0 space-y-1.5 overflow-y-auto pr-1">
              {displayedDisputes.map((item) => {
                const isSelected = selectedDisputeId === item.id;
                const isBatchChecked = selectedBatchIds.includes(item.id);
                const statusAccent =
                  item.status === "open"
                    ? "border-l-yellow-400/70"
                    : item.status === "under_review"
                      ? "border-l-blue-400/70"
                      : "border-l-green-400/70";
                return (
                  <div key={item.id} className={cn("flex items-start", isBatchMode && "gap-2")}>
                    {isBatchMode && (
                      <Checkbox
                        className="mt-1"
                        checked={isBatchChecked}
                        onChange={() => toggleBatchSelection(item.id)}
                        onClick={(event) => event.stopPropagation()}
                        aria-label={`Select dispute ${item.id}`}
                      />
                    )}
                    <Link
                      to="/app/clubs/$slug/admin/disputes"
                      params={{ slug }}
                      search={{
                        status: statusFilter,
                        sort: sortOrder,
                        claim: claimFilter,
                        claimer: claimerFilter,
                        dispute: item.id,
                      }}
                      replace
                      className={cn(
                        "block flex-1 rounded-lg border border-l-4 px-3 py-2.5 text-left leading-normal transition-all",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 focus-visible:ring-inset",
                        statusAccent,
                        isSelected
                          ? "border-brand-500/40 bg-brand-500/10"
                          : "border-surface-800/50 bg-surface-850/60 hover:bg-surface-800/70",
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <Badge variant="subtle" color={statusBadgeColor[item.status] ?? "gray"}>
                            {formatDisputeStatus(item.status)}
                          </Badge>
                          <Badge variant="subtle" color="gray">
                            {formatCategory(item.category)}
                          </Badge>
                        </div>
                        <span className="text-2xs tabular-nums text-surface-600">
                          #{item.id.slice(-8)}
                        </span>
                      </div>
                      <p className="mt-1.5 line-clamp-2 text-sm text-surface-100">{item.reason}</p>
                      <div className="mt-2 flex items-center gap-2 text-xs text-surface-500">
                        <span className="truncate">{item.reporterUsername || item.reporterId}</span>
                        <span className="text-surface-700">·</span>
                        <span className="shrink-0">{formatAgo(item.createdAt)}</span>
                        {item.evidenceCount > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-surface-400">
                            <Paperclip className="size-3" />
                            {item.evidenceCount}
                          </span>
                        )}
                        <span className="ml-auto shrink-0">
                          {item.isClaimStale ? (
                            <span className="font-semibold text-yellow-400">Stale</span>
                          ) : item.claimedById ? (
                            item.claimedById === session?.user?.id ? (
                              <span className="font-medium text-brand-400">Mine</span>
                            ) : (
                              <span className="text-blue-400">Claimed</span>
                            )
                          ) : (
                            <span className="text-surface-600">Open</span>
                          )}
                        </span>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard
        className="lg:col-span-2"
        header={
          <div className="flex items-center justify-between gap-2 px-4 py-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-white">Detail</h2>
            {selectedDispute && (
              <div className="flex items-center gap-2">
                <Badge variant="subtle" color={statusBadgeColor[selectedDispute.status] ?? "gray"}>
                  {formatDisputeStatus(selectedDispute.status)}
                </Badge>
                <span className="text-xs tabular-nums text-surface-500">
                  #{selectedDispute.id.slice(-8)}
                </span>
              </div>
            )}
          </div>
        }
      >
        {!selectedDisputeId ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex items-center justify-center size-12 rounded-xl bg-surface-800 border border-surface-700/50 mb-3">
              <Gavel className="size-6 text-surface-500" />
            </div>
            <p className="text-sm font-medium text-surface-300">No dispute selected</p>
            <p className="mt-1 text-xs text-surface-500">
              Select a dispute from the queue to review details and take action.
            </p>
          </div>
        ) : detailQuery.isLoading ? (
          <div className="space-y-3">
            <div className="h-8 rounded-lg bg-surface-800/40 animate-pulse" />
            <div className="h-20 rounded-lg bg-surface-800/40 animate-pulse" />
            <div className="h-40 rounded-lg bg-surface-800/40 animate-pulse" />
          </div>
        ) : detailQuery.error || !detailQuery.data ? (
          <div className="rounded-lg border border-error-500/30 bg-error-500/10 p-4 text-sm text-error-300">
            {detailQuery.error?.message || "Failed to load dispute detail."}
          </div>
        ) : (
          <DisputeDetailPanel
            slug={slug}
            clubId={club?.id ?? ""}
            selectedDispute={selectedDispute}
            detail={detailQuery.data}
            adminNotes={adminNotes}
            onAdminNotesChange={setAdminNotes}
            canModerate={canModerate}
            canResolve={canResolve}
            currentUserId={session?.user?.id ?? null}
            isResolving={resolveMutation.isPending}
            isUpdatingClaim={claimMutation.isPending || unclaimMutation.isPending}
            onResolve={handleResolve}
            onClaim={handleClaim}
            onUnclaim={handleUnclaim}
          />
        )}
      </SectionCard>
      <Modal
        open={pendingBatchResolution !== null}
        onClose={closeBatchResolutionModal}
        className="!max-w-xl"
      >
        <ModalHeader subtitle="Only technical disputes are eligible. This action will void selected matches.">
          Confirm Batch Resolution
        </ModalHeader>
        <ModalBody className="space-y-3">
          <div className="rounded-lg border border-surface-800/60 bg-surface-875 p-3">
            <p className="text-sm text-surface-200">
              Action: <span className="font-semibold text-surface-100">Void Selected</span>
            </p>
            <p className="mt-1 text-sm text-surface-400">
              {selectedBatchCount} dispute{selectedBatchCount === 1 ? "" : "s"} selected.
            </p>
            {selectedBatchDisputes.length > 0 && (
              <p className="mt-1 text-xs text-surface-500">
                IDs: {selectedBatchDisputes.map((item) => `#${item.id.slice(-8)}`).join(", ")}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-bold uppercase tracking-wider text-surface-400">
              Required Reason
            </p>
            <Textarea
              value={batchAdminNotes}
              onChange={(event) => setBatchAdminNotes(event.target.value)}
              placeholder="Explain why these disputes are being batch-voided (min 10 characters)."
              rows={3}
              resize="none"
            />
            <p className="text-xs text-surface-500">This reason will be written to each dispute.</p>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="md"
              onClick={closeBatchResolutionModal}
              disabled={resolveBatchMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="md"
              onClick={confirmBatchResolution}
              disabled={
                resolveBatchMutation.isPending ||
                selectedBatchCount === 0 ||
                batchAdminNotes.trim().length < 10
              }
            >
              {resolveBatchMutation.isPending ? "Applying..." : "Confirm Void Selected"}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
}

function DisputeDetailPanel({
  slug,
  clubId,
  selectedDispute,
  detail,
  adminNotes,
  onAdminNotesChange,
  canModerate,
  canResolve,
  currentUserId,
  isResolving,
  isUpdatingClaim,
  onResolve,
  onClaim,
  onUnclaim,
}: {
  slug: string;
  clubId: string;
  selectedDispute: DisputeListItem | null;
  detail: DisputeDetail;
  adminNotes: string;
  onAdminNotesChange: (value: string) => void;
  canModerate: boolean;
  canResolve: boolean;
  currentUserId: string | null;
  isResolving: boolean;
  isUpdatingClaim: boolean;
  onResolve: (
    resolution: ResolutionValue,
    correctedWinner?: number,
    correctionReason?: string,
  ) => void;
  onClaim: () => void;
  onUnclaim: () => void;
}) {
  const teams = useMemo(() => {
    const grouped = new Map<number, typeof detail.match.players>();
    for (const player of detail.match.players) {
      const list = grouped.get(player.team) ?? [];
      list.push(player);
      grouped.set(player.team, list);
    }
    return Array.from(grouped.entries()).sort((a, b) => a[0] - b[0]);
  }, [detail.match.players]);
  const teamNames = useMemo(
    () => getTeamNames(detail.match.id, Math.max(teams.length, 2)),
    [detail.match.id, teams.length],
  );
  const getTeamName = (teamIndex: number) => teamNames[teamIndex] ?? `Team ${teamIndex + 1}`;
  const isSoloTeams = useMemo(
    () => teams.length > 0 && teams.every(([, players]) => players.length === 1),
    [teams],
  );
  const soloNameByTeam = useMemo(() => {
    const map = new Map<number, string>();
    for (const [team, players] of teams) {
      const player = players[0];
      if (player) {
        map.set(team, player.username || player.userId);
      }
    }
    return map;
  }, [teams]);
  const getWinnerLabel = (teamIndex: number) =>
    isSoloTeams
      ? (soloNameByTeam.get(teamIndex) ?? getTeamName(teamIndex))
      : getTeamName(teamIndex);
  const rankThresholds = useMemo(
    () =>
      getMergedThresholds(
        (detail.match.rankConfig as Parameters<typeof getMergedThresholds>[0]) ?? null,
      ),
    [detail.match.rankConfig],
  );
  const getPlayerRank = (elo: number) => {
    const rank = getRankFromElo(elo, rankThresholds);
    const level = getRankLevel(elo, rank, rankThresholds);
    return { rank, level };
  };
  const winnerVoteTotals = useMemo(() => {
    const voteCounts = new Map<number, number>();
    for (const player of detail.match.players) {
      if (player.reportedWinner === null) continue;
      voteCounts.set(player.reportedWinner, (voteCounts.get(player.reportedWinner) ?? 0) + 1);
    }

    const totalReports = Array.from(voteCounts.values()).reduce((sum, count) => sum + count, 0);
    const byTeam = Array.from(voteCounts.entries())
      .sort((a, b) => b[1] - a[1] || a[0] - b[0])
      .map(([team, votes]) => ({
        team,
        votes,
        percent: totalReports > 0 ? Math.round((votes / totalReports) * 100) : 0,
      }));

    return { totalReports, byTeam };
  }, [detail.match.players]);
  const utils = trpc.useUtils();
  const [selectedOverturnTeam, setSelectedOverturnTeam] = useState<string>("0");
  const [pendingResolution, setPendingResolution] = useState<PendingResolution | null>(null);
  const [correctionReason, setCorrectionReason] = useState("");
  const [evidenceLinkUrl, setEvidenceLinkUrl] = useState("");
  const [isUploadingEvidence, setIsUploadingEvidence] = useState(false);
  const [isMatchSummaryOpen, setIsMatchSummaryOpen] = useState(false);
  const [showAllTeams, setShowAllTeams] = useState(false);
  const [isUploadAreaOpen, setIsUploadAreaOpen] = useState(false);
  const teamCap = isSoloTeams ? 6 : 4;
  const needsTeamCap = teams.length > teamCap;
  const visibleTeams = showAllTeams || !needsTeamCap ? teams : teams.slice(0, teamCap);
  const totalPlayers = detail.match.players.length;
  const isResolved = detail.dispute.status === "resolved";
  const addEvidenceMutation = trpc.dispute.addEvidence.useMutation();
  const removeEvidenceMutation = trpc.dispute.removeEvidence.useMutation();
  const previewQuery = trpc.dispute.preview.useQuery(
    {
      disputeId: detail.dispute.id,
      clubId,
      resolution: pendingResolution?.resolution ?? "upheld",
      correctedWinner: pendingResolution?.correctedWinner,
    },
    {
      enabled: !!clubId && pendingResolution !== null && canResolve,
      retry: false,
    },
  );
  const reporterName =
    detail.reporter?.name || selectedDispute?.reporterUsername || selectedDispute?.reporterId;
  const reporterUserId = detail.reporter?.id || selectedDispute?.reporterId;
  const reporterAvatar = detail.reporter?.avatarUrl ?? null;
  const claimedById = detail.dispute.claimedById ?? null;
  const claimedByName =
    detail.claimer?.name || (claimedById ? `User ${claimedById.slice(-8)}` : null);
  const isClaimedByCurrentUser = !!currentUserId && claimedById === currentUserId;
  const isClaimed = claimedById !== null;
  const resolutionHistory = detail.resolutionHistory ?? [];
  const timeline = detail.timeline ?? [];
  const [actionModalPlayer, setActionModalPlayer] = useState<{
    userId: string;
    username: string;
    avatarUrl: string | null;
    eloAtMatch: number;
  } | null>(null);

  useEffect(() => {
    setSelectedOverturnTeam(String(teams[0]?.[0] ?? 0));
  }, [teams]);
  useEffect(() => {
    setPendingResolution(null);
    setCorrectionReason("");
    setEvidenceLinkUrl("");
    setIsMatchSummaryOpen(false);
    setShowAllTeams(false);
    setIsUploadAreaOpen(false);
  }, [detail.dispute.id]);

  const requestResolve = (resolution: ResolutionValue, correctedWinner?: number) => {
    if (isResolved) {
      setCorrectionReason("");
    }
    setPendingResolution({ resolution, correctedWinner });
  };
  const closeResolveModal = () => {
    if (isResolving) return;
    setPendingResolution(null);
    setCorrectionReason("");
  };
  const isCorrection = isResolved;
  const hasValidCorrectionReason = !isCorrection || correctionReason.trim().length >= 10;
  const confirmResolve = () => {
    if (!pendingResolution || !canConfirmResolution) return;
    onResolve(
      pendingResolution.resolution,
      pendingResolution.correctedWinner,
      isCorrection ? correctionReason : undefined,
    );
    setPendingResolution(null);
    setCorrectionReason("");
  };
  const pendingResolutionLabel = pendingResolution
    ? formatDisputeResolution(pendingResolution.resolution)
    : null;
  const pendingResolutionTarget =
    pendingResolution?.resolution === "overturned" &&
    pendingResolution.correctedWinner !== undefined &&
    pendingResolution.correctedWinner !== null
      ? getWinnerLabel(pendingResolution.correctedWinner)
      : null;
  const canConfirmResolution =
    pendingResolution !== null &&
    !isResolving &&
    !previewQuery.isLoading &&
    !previewQuery.error &&
    hasValidCorrectionReason;
  const previewPlayers = previewQuery.data?.ratingImpact?.players ?? [];
  const previewByUserId = useMemo(
    () =>
      new Map(
        detail.match.players.map((player) => [player.userId, player.username || player.userId]),
      ),
    [detail.match.players],
  );
  const previewPositivePlayers = useMemo(
    () => previewPlayers.filter((player) => player.displayEloDelta > 0),
    [previewPlayers],
  );
  const previewNegativePlayers = useMemo(
    () => previewPlayers.filter((player) => player.displayEloDelta < 0),
    [previewPlayers],
  );
  const previewNeutralPlayers = useMemo(
    () => previewPlayers.filter((player) => player.displayEloDelta === 0),
    [previewPlayers],
  );
  const omittedPlayerCount = previewQuery.data?.ratingImpact?.omittedUserIds.length ?? 0;
  const previewPopulationCount = previewPlayers.length + omittedPlayerCount;
  const hasNoPreviewCoverage = previewPlayers.length === 0 && omittedPlayerCount > 0;
  const previewNetDelta = useMemo(
    () => previewPlayers.reduce((sum, player) => sum + player.displayEloDelta, 0),
    [previewPlayers],
  );
  const previewTeamDeltas = useMemo(() => {
    const teamDeltaMap = new Map<number, number>();
    for (const player of previewPlayers) {
      teamDeltaMap.set(player.team, (teamDeltaMap.get(player.team) ?? 0) + player.displayEloDelta);
    }
    return Array.from(teamDeltaMap.entries()).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
  }, [previewPlayers]);
  const isEvidenceBusy =
    isUploadingEvidence || addEvidenceMutation.isPending || removeEvidenceMutation.isPending;

  const uploadEvidenceFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileList = Array.from(files);
    const kindByMime = (mimeType: string): "image" | "video" | "log" | null => {
      if (mimeType.startsWith("image/")) return "image";
      if (mimeType.startsWith("video/")) return "video";
      if (mimeType === "text/plain" || mimeType === "application/json") return "log";
      return null;
    };

    setIsUploadingEvidence(true);
    try {
      for (const file of fileList) {
        const kind = kindByMime(file.type);
        if (!kind) {
          toast.error(`Unsupported evidence type: ${file.name}`);
          continue;
        }

        const result = await uploadFile(file, "dispute-evidence", detail.dispute.id, kind);

        await addEvidenceMutation.mutateAsync({
          disputeId: detail.dispute.id,
          kind,
          storageKey: result.key,
          mimeType: file.type,
          sizeBytes: file.size,
        });
      }

      await utils.dispute.getDetail.invalidate({ disputeId: detail.dispute.id });
      toast.success("Evidence uploaded");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to upload evidence");
    } finally {
      setIsUploadingEvidence(false);
    }
  };

  const addLinkEvidence = async () => {
    const trimmed = evidenceLinkUrl.trim();
    if (!trimmed) return;

    try {
      await addEvidenceMutation.mutateAsync({
        disputeId: detail.dispute.id,
        kind: "link",
        externalUrl: trimmed,
      });
      setEvidenceLinkUrl("");
      await utils.dispute.getDetail.invalidate({ disputeId: detail.dispute.id });
      toast.success("Link evidence added");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to add evidence link");
    }
  };

  const removeEvidence = async (evidenceId: string) => {
    try {
      await removeEvidenceMutation.mutateAsync({ evidenceId });
      await utils.dispute.getDetail.invalidate({ disputeId: detail.dispute.id });
      toast.success("Evidence removed");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to remove evidence");
    }
  };

  return (
    <div className="space-y-4">
      {/* Reporter & Reason */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <Avatar
              src={reporterAvatar}
              alt={reporterName}
              initials={reporterName?.slice(0, 1)}
              size="sm"
            />
            <div className="min-w-0">
              {reporterUserId ? (
                <Link
                  to="/app/users/$slug"
                  params={{ slug: detail.reporter?.slug ?? reporterUserId }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate text-sm font-semibold text-surface-100 hover:text-brand-300"
                  title={reporterName}
                >
                  {reporterName}
                </Link>
              ) : (
                <p className="truncate text-sm font-semibold text-surface-100" title={reporterName}>
                  {reporterName}
                </p>
              )}
              <p className="text-xs text-surface-500">{formatAgo(detail.dispute.createdAt)}</p>
            </div>
          </div>
          <Link
            to="/app/match/$shortId"
            params={{ shortId: detail.match.shortId ?? detail.match.id }}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-surface-800/60 bg-surface-875 px-2.5 py-1.5 text-xs text-brand-400 hover:text-brand-300 hover:border-brand-500/30 transition-colors"
          >
            Open Match
            <ExternalLink className="size-3" />
          </Link>
        </div>
        <div className="rounded-lg border border-surface-800/40 bg-surface-900/30 p-3">
          <p className="text-sm text-surface-200 whitespace-pre-wrap break-words leading-relaxed">
            {detail.dispute.reason}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {detail.dispute.resolution && (
            <Badge
              variant="subtle"
              color={resolutionBadgeColor[detail.dispute.resolution] ?? "gray"}
            >
              {formatDisputeResolution(detail.dispute.resolution)}
            </Badge>
          )}
          <Badge variant="subtle" color={claimedById ? "blue" : "gray"}>
            {claimedById ? "Claimed" : "Unclaimed"}
          </Badge>
          <Badge variant="subtle" color="gray">
            {formatCategory(detail.dispute.category)}
          </Badge>
        </div>
      </div>

      <div className="rounded-xl border border-surface-800/50 bg-surface-850/60 p-3 space-y-3">
        <button
          type="button"
          onClick={() => setIsMatchSummaryOpen((prev) => !prev)}
          className="flex w-full items-center justify-between gap-2"
        >
          <div className="flex items-center gap-2">
            <p className="text-xs font-bold uppercase tracking-wider text-surface-400">
              Match Summary
            </p>
            <span className="text-xs text-surface-500">
              {teams.length} {isSoloTeams ? "players" : `teams \u00b7 ${totalPlayers} players`}
            </span>
          </div>
          <ChevronDown
            className={cn(
              "size-4 text-surface-400 transition-transform duration-200",
              isMatchSummaryOpen && "rotate-180",
            )}
          />
        </button>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-surface-400">
          <span className="inline-flex items-center gap-1">
            Queue:
            {detail.match.queueId ? (
              <Link
                to="/app/clubs/$slug/queues/$queueSlug"
                params={{ slug, queueSlug: detail.match.queueSlug ?? detail.match.queueId }}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-400 hover:text-brand-300 hover:underline"
              >
                {detail.match.queueName || "Unknown"}
              </Link>
            ) : (
              <span>{detail.match.queueName || "Unknown"}</span>
            )}
          </span>
          <span>Map: {detail.match.mapName || "Unassigned"}</span>
          <span
            className={cn(
              detail.match.winningTeam !== null && getTeamColor(detail.match.winningTeam).text,
            )}
          >
            Declared Winner: {renderWinner(detail.match.winningTeam, getWinnerLabel)}
          </span>
        </div>
        <AnimatePresence initial={false}>
          {isMatchSummaryOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="space-y-3 overflow-hidden"
            >
              {winnerVoteTotals.totalReports > 0 && (
                <div className="rounded-lg border border-surface-800/60 bg-surface-900/30 p-2.5">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-surface-400">
                      Winner Votes
                    </p>
                    <span className="text-xs text-surface-500">
                      {winnerVoteTotals.totalReports} reports
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {winnerVoteTotals.byTeam.slice(0, 12).map((vote) => {
                      const color = getTeamColor(vote.team);
                      return (
                        <span
                          key={vote.team}
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs font-semibold",
                            color.border,
                            color.text,
                          )}
                        >
                          <span className={cn("size-1.5 rounded-full", color.accent)} />
                          {getWinnerLabel(vote.team)} {vote.votes} ({vote.percent}%)
                        </span>
                      );
                    })}
                  </div>
                  {winnerVoteTotals.byTeam.length > 12 && (
                    <p className="mt-2 text-xs text-surface-500">
                      +{winnerVoteTotals.byTeam.length - 12} more teams received votes.
                    </p>
                  )}
                </div>
              )}
              <div className="grid gap-3 md:grid-cols-2">
                {visibleTeams.map(([team, players]) => {
                  const teamColor = getTeamColor(team);
                  const soloPlayer = isSoloTeams ? players[0] : null;
                  const soloReportedWinner = soloPlayer?.reportedWinner ?? null;
                  const soloReportedLabel =
                    soloReportedWinner === null
                      ? null
                      : `Reported ${getWinnerLabel(soloReportedWinner)}`;
                  return (
                    <div
                      key={team}
                      className={cn("rounded-lg border bg-surface-875 p-3", teamColor.border)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className={cn(
                            "font-bold",
                            isSoloTeams
                              ? "text-sm tracking-normal"
                              : "text-xs uppercase tracking-wider",
                            teamColor.text,
                          )}
                        >
                          {getWinnerLabel(team)}
                        </p>
                        {isSoloTeams &&
                          (soloReportedLabel === null ? (
                            <Badge variant="outline" color="gray">
                              No report
                            </Badge>
                          ) : (
                            <span
                              title={soloReportedLabel}
                              className={cn(
                                "inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-xs font-semibold sm:shrink-0",
                                getTeamColor(soloReportedWinner!).border,
                                getTeamColor(soloReportedWinner!).bg,
                                getTeamColor(soloReportedWinner!).text,
                              )}
                            >
                              <span className="truncate">{soloReportedLabel}</span>
                            </span>
                          ))}
                      </div>
                      <div className="mt-2 space-y-2">
                        {players.map((player) => {
                          const rank = getPlayerRank(player.eloAtMatch);
                          const playerDisplayName = player.username || player.userId;
                          return (
                            <div
                              key={player.userId}
                              className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div className="flex min-w-0 flex-1 items-center gap-2">
                                <Avatar
                                  src={player.avatarUrl}
                                  alt={playerDisplayName}
                                  initials={playerDisplayName.slice(0, 1)}
                                  size="sm"
                                />
                                <RankIcon
                                  rank={rank.rank}
                                  level={rank.level}
                                  size="xs"
                                  showLevels
                                  levelLayout="right"
                                />
                                <div className="min-w-0">
                                  <PlayerQuickInfoPopover
                                    slug={slug}
                                    clubId={clubId}
                                    userId={player.userId}
                                    userSlug={player.slug}
                                    username={playerDisplayName}
                                    avatarUrl={player.avatarUrl}
                                    eloAtMatch={player.eloAtMatch}
                                    onModerate={
                                      canModerate
                                        ? () =>
                                            setActionModalPlayer({
                                              userId: player.userId,
                                              username: playerDisplayName,
                                              avatarUrl: player.avatarUrl,
                                              eloAtMatch: player.eloAtMatch,
                                            })
                                        : undefined
                                    }
                                  />
                                  <p className="text-xs text-surface-500">
                                    ELO {player.eloAtMatch}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5">
                                {!isSoloTeams &&
                                  (player.reportedWinner === null ? (
                                    <Tooltip content="No winner report submitted" side="left">
                                      <Badge variant="outline" color="gray">
                                        —
                                      </Badge>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip
                                      content={`Reported winner: ${getWinnerLabel(player.reportedWinner!)}`}
                                      side="left"
                                    >
                                      <span
                                        className={cn(
                                          "inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-xs font-semibold sm:shrink-0",
                                          getTeamColor(player.reportedWinner!).border,
                                          getTeamColor(player.reportedWinner!).bg,
                                          getTeamColor(player.reportedWinner!).text,
                                        )}
                                      >
                                        <span className="truncate">
                                          {getWinnerLabel(player.reportedWinner!)}
                                        </span>
                                      </span>
                                    </Tooltip>
                                  ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              {needsTeamCap && (
                <button
                  type="button"
                  onClick={() => setShowAllTeams(!showAllTeams)}
                  className="w-full rounded-lg border border-surface-800/60 bg-surface-900/30 px-3 py-2 text-xs font-medium text-surface-300 hover:text-brand-300 hover:border-brand-500/30 transition-colors"
                >
                  {showAllTeams
                    ? "Show fewer"
                    : `Show all ${teams.length} ${isSoloTeams ? "players" : "teams"} (+${teams.length - teamCap} more)`}
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="rounded-xl border border-surface-800/50 bg-surface-850/60 p-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-bold uppercase tracking-wider text-surface-400">Evidence</p>
          <Badge variant="subtle" color={detail.evidence.length > 0 ? "blue" : "gray"}>
            {detail.evidence.length}
          </Badge>
        </div>

        {detail.evidence.length === 0 ? (
          <p className="text-sm text-surface-400">No evidence attached yet.</p>
        ) : (
          <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
            {detail.evidence.map((item) => {
              const ev = evidenceMeta(item.kind, item.mimeType);
              return (
                <div
                  key={item.id}
                  className={cn(
                    "rounded-lg border p-2.5",
                    item.deletedAt
                      ? "border-yellow-500/20 bg-yellow-500/5"
                      : "border-surface-800/60 bg-surface-875",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div
                        className={cn(
                          "flex size-7 shrink-0 items-center justify-center rounded-md",
                          ev.bgClass,
                        )}
                      >
                        <ev.icon className={cn("size-3.5", ev.iconClass)} />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-surface-200">
                          {ev.label}
                          {item.sizeBytes !== null && item.kind !== "link" && (
                            <span className="ml-1.5 font-normal text-surface-500">
                              {formatBytes(item.sizeBytes)}
                            </span>
                          )}
                        </p>
                        <p className="truncate text-xs text-surface-500">
                          {item.uploadedByUsername || item.uploadedByUserId} •{" "}
                          {formatAgo(item.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {!item.deletedAt && item.kind === "link" && item.externalUrl && (
                        <a
                          href={item.externalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-brand-400 hover:text-brand-300 hover:bg-surface-800/40 transition-colors"
                        >
                          {formatDomain(item.externalUrl)}
                          <ExternalLink className="size-3" />
                        </a>
                      )}
                      {!item.deletedAt && item.kind !== "link" && item.downloadUrl && (
                        <EvidenceViewButton evidenceId={item.id} />
                      )}
                      {!item.deletedAt && (
                        <Button
                          variant="ghost"
                          disabled={isEvidenceBusy}
                          onClick={() => void removeEvidence(item.id)}
                          className="!p-1 !size-auto rounded-md text-surface-500 hover:text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {item.deletedAt && (
                    <p className="mt-1 text-xs text-yellow-300/80">
                      Removed {formatAgo(item.deletedAt)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!isUploadAreaOpen ? (
          <Button variant="outline" size="sm" onClick={() => setIsUploadAreaOpen(true)}>
            <Paperclip className="size-3.5 mr-1.5" />
            Attach Evidence
          </Button>
        ) : (
          <div className="space-y-2 rounded-lg border border-surface-800/60 bg-surface-900/30 p-2.5">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-surface-500">
                Attach screenshots, clips, logs, or links for moderation context.
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsUploadAreaOpen(false)}
                className="!px-1.5 !py-0.5 text-xs text-surface-500 hover:text-surface-300"
              >
                Close
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold",
                  isEvidenceBusy
                    ? "cursor-not-allowed border-surface-800 text-surface-500"
                    : "cursor-pointer border-surface-700 text-surface-200 hover:border-brand-500/50 hover:text-brand-300",
                )}
              >
                <Paperclip className="size-3.5" />
                Upload Files
                <input
                  type="file"
                  multiple
                  className="hidden"
                  accept="image/*,video/*,text/plain,application/json"
                  disabled={isEvidenceBusy}
                  onChange={(event) => {
                    void uploadEvidenceFiles(event.target.files);
                    event.currentTarget.value = "";
                  }}
                />
              </label>
              <span className="text-xs text-surface-500">Max 50 MB each</span>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Input
                value={evidenceLinkUrl}
                onChange={(event) => setEvidenceLinkUrl(event.target.value)}
                placeholder="https://example.com/evidence"
                disabled={isEvidenceBusy}
              />
              <Button
                variant="outline"
                size="sm"
                disabled={isEvidenceBusy || evidenceLinkUrl.trim().length === 0}
                onClick={() => void addLinkEvidence()}
              >
                Add Link
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Admin Notes */}
      <div className="space-y-1.5">
        <p className="text-xs font-bold uppercase tracking-wider text-surface-400">Admin Notes</p>
        <Textarea
          value={adminNotes}
          onChange={(event) => onAdminNotesChange(event.target.value)}
          placeholder="Internal moderation notes..."
          rows={3}
          resize="none"
        />
      </div>

      {/* Resolution History */}
      {resolutionHistory.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-surface-400">Audit Trail</p>
          <div className="max-h-40 space-y-1.5 overflow-y-auto pr-1">
            {resolutionHistory.map((entry) => (
              <div
                key={entry.id}
                className={cn(
                  "rounded-lg border p-2.5",
                  entry.correctionReason
                    ? "border-yellow-500/25 bg-yellow-500/5"
                    : "border-surface-800/60 bg-surface-875",
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-surface-200">
                    {entry.previousResolution
                      ? formatDisputeResolution(entry.previousResolution)
                      : "Unresolved"}{" "}
                    <span className="text-surface-500">→</span>{" "}
                    {formatDisputeResolution(entry.newResolution)}
                  </p>
                  <span className="shrink-0 text-xs text-surface-500">
                    {formatAgo(entry.createdAt)}
                  </span>
                </div>
                <p className="text-xs text-surface-500">
                  {entry.changedByUsername || entry.changedById}
                </p>
                {entry.correctionReason && (
                  <p className="mt-1 text-xs text-yellow-300/80">{entry.correctionReason}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {timeline.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-surface-400">
            Full Timeline
          </p>
          <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
            {timeline.map((entry) => (
              <div
                key={entry.id}
                className="rounded-lg border border-surface-800/60 bg-surface-875 p-2.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-surface-200">
                    {formatTimelineEvent(entry.eventType)}
                    <span className="ml-1.5 text-xs text-surface-500">
                      {formatTimelineActor(entry.actorUsername, entry.actorUserId)}
                    </span>
                  </p>
                  <span className="shrink-0 text-xs text-surface-500">
                    {formatAgo(entry.createdAt)}
                  </span>
                </div>
                {formatTimelineContext(entry.metadata) && (
                  <p className="mt-1 text-xs text-surface-500">
                    {formatTimelineContext(entry.metadata)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="rounded-xl border border-surface-800/50 bg-surface-850/60 p-3 space-y-3">
        {canResolve ? (
          !isResolved && !isClaimed ? (
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-surface-400">
                  Claim to Begin
                </p>
                <p className="text-xs text-surface-500 mt-0.5">
                  Claim this dispute to unlock moderation actions.
                </p>
              </div>
              <Button variant="primary" size="sm" disabled={isUpdatingClaim} onClick={onClaim}>
                {isUpdatingClaim ? "Claiming..." : "Claim Dispute"}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-bold uppercase tracking-wider text-surface-400">
                  {isResolved ? "Correct Resolution" : "Resolve Dispute"}
                </p>
                {!isResolved && isClaimed && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-surface-500">
                      {isClaimedByCurrentUser ? "Claimed by you" : `Claimed by ${claimedByName}`}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onUnclaim}
                      disabled={isUpdatingClaim}
                      className="!px-1.5 !py-0.5 text-xs text-surface-500 hover:text-surface-300 underline decoration-surface-700"
                    >
                      {isUpdatingClaim ? "Releasing..." : "Release"}
                    </Button>
                  </div>
                )}
              </div>
              {isResolved && (
                <p className="text-xs text-yellow-300/80">
                  Already resolved — corrections are tracked in the audit trail.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={isResolving || isUpdatingClaim}
                  onClick={() => requestResolve("upheld")}
                >
                  Uphold
                </Button>
                {teams.length <= 4 ? (
                  teams.map(([team]) => (
                    <Button
                      key={team}
                      variant="outline"
                      size="sm"
                      disabled={isResolving || isUpdatingClaim}
                      onClick={() => requestResolve("overturned", team)}
                    >
                      Overturn → {getWinnerLabel(team)}
                    </Button>
                  ))
                ) : (
                  <>
                    <div className="w-full sm:w-48">
                      <Select
                        options={teams.map(([team]) => ({
                          value: String(team),
                          label: getWinnerLabel(team),
                        }))}
                        value={selectedOverturnTeam}
                        onChange={setSelectedOverturnTeam}
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isResolving || isUpdatingClaim}
                      onClick={() => requestResolve("overturned", Number(selectedOverturnTeam))}
                    >
                      Overturn → {getWinnerLabel(Number(selectedOverturnTeam))}
                    </Button>
                  </>
                )}
                <Button
                  variant="danger"
                  size="sm"
                  disabled={isResolving || isUpdatingClaim}
                  onClick={() => requestResolve("cancelled")}
                >
                  Void Match
                </Button>
              </div>
            </div>
          )
        ) : (
          <p className="text-sm text-surface-400">
            Moderators can review disputes, but only admins/owners can resolve.
          </p>
        )}
      </div>

      {actionModalPlayer && (
        <PlayerActionModal
          player={actionModalPlayer}
          onClose={() => setActionModalPlayer(null)}
          clubId={clubId}
          disputeId={detail.dispute.id}
          matchId={detail.match.id}
          canResolve={canResolve}
          slug={slug}
          getPlayerRank={getPlayerRank}
        />
      )}

      <Modal open={pendingResolution !== null} onClose={closeResolveModal} className="!max-w-3xl">
        <ModalHeader subtitle="Review projected impact, then confirm this moderation action.">
          Confirm Resolution
        </ModalHeader>
        <ModalBody className="space-y-4">
          <div
            className={cn(
              "rounded-xl border p-3 text-sm",
              pendingResolution?.resolution === "cancelled"
                ? "border-error-500/40 bg-error-500/10"
                : pendingResolution?.resolution === "upheld"
                  ? "border-green-500/35 bg-green-500/10"
                  : "border-blue-500/35 bg-blue-500/10",
            )}
          >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <p className="text-surface-300">
                Action:{" "}
                <span className="font-semibold text-surface-100">{pendingResolutionLabel}</span>
              </p>
              {pendingResolutionTarget && (
                <p className="text-surface-300">
                  Target winner:{" "}
                  <span className="font-semibold text-surface-100">{pendingResolutionTarget}</span>
                </p>
              )}
              <p className="text-xs text-surface-500">Dispute #{detail.dispute.id.slice(-8)}</p>
            </div>
          </div>
          {previewQuery.isLoading ? (
            <div className="space-y-2">
              <div className="h-12 rounded-lg bg-surface-800/40 animate-pulse" />
              <div className="h-24 rounded-lg bg-surface-800/40 animate-pulse" />
              <div className="h-24 rounded-lg bg-surface-800/40 animate-pulse" />
            </div>
          ) : previewQuery.error ? (
            <div className="rounded-lg border border-error-500/30 bg-error-500/10 p-3 text-sm text-error-300">
              {previewQuery.error.message}
            </div>
          ) : previewQuery.data?.ratingImpact ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-bold uppercase tracking-wider text-surface-500">
                  Projected ELO Impact
                </p>
                {previewPopulationCount > 0 && (
                  <span className="text-xs text-surface-500">
                    Coverage {previewPlayers.length}/{previewPopulationCount} players
                  </span>
                )}
              </div>
              {hasNoPreviewCoverage ? (
                <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 space-y-1.5">
                  <p className="text-sm font-semibold text-yellow-300">
                    Rating preview unavailable
                  </p>
                  <p className="text-sm text-yellow-200">
                    None of this match&apos;s players have current rating rows in the queue, so ELO
                    deltas cannot be calculated.
                  </p>
                  <p className="text-xs text-yellow-200/80">
                    Confirming will still resolve the dispute and update the match winner, but no
                    rating/stat adjustments can be applied.
                  </p>
                </div>
              ) : previewPlayers.length > 0 ? (
                <div className="space-y-3">
                  <div className="grid gap-2 sm:grid-cols-4">
                    <div className="rounded-lg border border-surface-800/60 bg-surface-875 px-2.5 py-2">
                      <p className="text-2xs uppercase tracking-wider text-surface-500">Affected</p>
                      <p className="text-sm font-semibold text-surface-100">
                        {previewPlayers.length}
                      </p>
                    </div>
                    <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-2.5 py-2">
                      <p className="text-2xs uppercase tracking-wider text-green-300/80">Gaining</p>
                      <p className="text-sm font-semibold text-green-300">
                        {previewPositivePlayers.length}
                      </p>
                    </div>
                    <div className="rounded-lg border border-error-500/30 bg-error-500/10 px-2.5 py-2">
                      <p className="text-2xs uppercase tracking-wider text-error-300/80">Losing</p>
                      <p className="text-sm font-semibold text-error-300">
                        {previewNegativePlayers.length}
                      </p>
                    </div>
                    <div className="rounded-lg border border-surface-800/60 bg-surface-875 px-2.5 py-2">
                      <p className="text-2xs uppercase tracking-wider text-surface-500">
                        Net Delta
                      </p>
                      <p
                        className={cn(
                          "text-sm font-semibold tabular-nums",
                          previewNetDelta > 0
                            ? "text-green-400"
                            : previewNetDelta < 0
                              ? "text-error-400"
                              : "text-surface-300",
                        )}
                      >
                        {formatSignedNumber(previewNetDelta)}
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-2 md:grid-cols-2">
                    <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-2.5">
                      <p className="text-2xs font-bold uppercase tracking-wider text-green-300/90">
                        Winners of Adjustment
                      </p>
                      <div className="mt-2 max-h-52 space-y-1.5 overflow-y-auto pr-1">
                        {previewPositivePlayers.length === 0 ? (
                          <p className="text-xs text-green-300/70">No positive ELO changes.</p>
                        ) : (
                          previewPositivePlayers.map((player) => {
                            const displayName = previewByUserId.get(player.userId) ?? player.userId;
                            return (
                              <div
                                key={player.userId}
                                className="flex items-center justify-between gap-2 rounded-md border border-green-500/20 bg-surface-875 px-2 py-1.5"
                              >
                                <div className="min-w-0">
                                  <p className="truncate text-sm text-surface-100">{displayName}</p>
                                  <p className="text-xs text-surface-500">
                                    {getWinnerLabel(player.team)} ({player.currentDisplayElo} →{" "}
                                    {player.projectedDisplayElo})
                                  </p>
                                </div>
                                <p className="text-sm font-semibold text-green-300 tabular-nums">
                                  {formatSignedNumber(player.displayEloDelta)}
                                </p>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>

                    <div className="rounded-lg border border-error-500/30 bg-error-500/10 p-2.5">
                      <p className="text-2xs font-bold uppercase tracking-wider text-error-300/90">
                        Losers of Adjustment
                      </p>
                      <div className="mt-2 max-h-52 space-y-1.5 overflow-y-auto pr-1">
                        {previewNegativePlayers.length === 0 ? (
                          <p className="text-xs text-error-300/70">No negative ELO changes.</p>
                        ) : (
                          previewNegativePlayers.map((player) => {
                            const displayName = previewByUserId.get(player.userId) ?? player.userId;
                            return (
                              <div
                                key={player.userId}
                                className="flex items-center justify-between gap-2 rounded-md border border-error-500/20 bg-surface-875 px-2 py-1.5"
                              >
                                <div className="min-w-0">
                                  <p className="truncate text-sm text-surface-100">{displayName}</p>
                                  <p className="text-xs text-surface-500">
                                    {getWinnerLabel(player.team)} ({player.currentDisplayElo} →{" "}
                                    {player.projectedDisplayElo})
                                  </p>
                                </div>
                                <p className="text-sm font-semibold text-error-300 tabular-nums">
                                  {formatSignedNumber(player.displayEloDelta)}
                                </p>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>

                  {previewTeamDeltas.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {previewTeamDeltas.map(([team, delta]) => (
                        <span
                          key={team}
                          className={cn(
                            "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold",
                            delta > 0
                              ? "border-green-500/35 bg-green-500/10 text-green-300"
                              : delta < 0
                                ? "border-error-500/35 bg-error-500/10 text-error-300"
                                : "border-surface-800/60 bg-surface-875 text-surface-300",
                          )}
                        >
                          {getWinnerLabel(team)} {formatSignedNumber(delta)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-surface-400">
                  No rating changes were projected for this action.
                </p>
              )}
              {previewNeutralPlayers.length > 0 && (
                <p className="text-xs text-surface-500">
                  {previewNeutralPlayers.length} player(s) had no ELO delta.
                </p>
              )}
              {omittedPlayerCount > 0 && (
                <p className="text-xs text-surface-500">
                  {omittedPlayerCount} player(s) omitted due to missing current rating rows.
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-200">
              No rating changes will be applied for this action.
            </div>
          )}
          {isCorrection && (
            <div className="space-y-1.5">
              <p className="text-xs font-bold uppercase tracking-wider text-surface-400">
                Correction Reason
              </p>
              <Textarea
                value={correctionReason}
                onChange={(event) => setCorrectionReason(event.target.value)}
                placeholder="Explain why this resolved dispute is being corrected (required)."
                rows={3}
                resize="none"
              />
              <p
                className={cn(
                  "text-xs",
                  hasValidCorrectionReason ? "text-surface-500" : "text-yellow-400",
                )}
              >
                {hasValidCorrectionReason
                  ? "Reason recorded in the dispute audit trail."
                  : "At least 10 characters are required to apply a correction."}
              </p>
            </div>
          )}
          <p className="text-sm text-surface-400">
            This will finalize the selected resolution and update dispute history immediately.
          </p>
        </ModalBody>
        <ModalFooter>
          <div className="flex w-full items-center justify-end gap-2">
            <Button variant="ghost" size="md" onClick={closeResolveModal} disabled={isResolving}>
              Cancel
            </Button>
            <Button
              variant={pendingResolution?.resolution === "cancelled" ? "danger" : "primary"}
              size="md"
              onClick={confirmResolve}
              disabled={!canConfirmResolution}
            >
              {isResolving ? "Applying..." : `Confirm ${pendingResolutionLabel ?? "Action"}`}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
}

function PlayerActionModal({
  player,
  onClose,
  clubId,
  disputeId,
  matchId,
  canResolve,
  slug,
  getPlayerRank,
}: {
  player: {
    userId: string;
    username: string;
    avatarUrl: string | null;
    eloAtMatch: number;
  };
  onClose: () => void;
  clubId: string;
  disputeId: string;
  matchId: string;
  canResolve: boolean;
  slug: string;
  getPlayerRank: (elo: number) => { rank: string; level: number };
}) {
  const utils = trpc.useUtils();
  const rank = getPlayerRank(player.eloAtMatch);

  // Warn state
  const [warnReason, setWarnReason] = useState("");

  // Queue Ban state
  const [queueBanReasonValue, setQueueBanReasonValue] = useState<string>("abuse");
  const [queueBanDetails, setQueueBanDetails] = useState("");

  // Club Ban state
  const [clubBanReason, setClubBanReason] = useState("");
  const [clubBanPermanent, setClubBanPermanent] = useState(true);
  const [clubBanDuration, setClubBanDuration] = useState<string>("7d");

  // Lazy-load player moderation summary
  const playerHistoryQuery = trpc.dispute.getPlayerHistory.useQuery(
    { clubId, userId: player.userId },
    { enabled: !!clubId },
  );
  const summary = playerHistoryQuery.data?.summary;

  // Mutations
  const warnMutation = trpc.dispute.warnParticipant.useMutation({
    onSuccess: async () => {
      toast.success("Warning issued");
      await Promise.all([
        utils.dispute.getPlayerHistory.invalidate({ clubId, userId: player.userId }),
        utils.dispute.getDetail.invalidate({ disputeId }),
      ]);
      setWarnReason("");
    },
    onError: (error) => toast.error(error.message || "Failed to issue warning"),
  });

  const queueBanMutation = trpc.clubBan.createQueueBan.useMutation({
    onSuccess: async () => {
      toast.success("Queue ban applied");
      await utils.dispute.getPlayerHistory.invalidate({ clubId, userId: player.userId });
      setQueueBanDetails("");
    },
    onError: (error) => toast.error(error.message || "Failed to apply queue ban"),
  });

  const clubBanMutation = trpc.clubBan.createClubBan.useMutation({
    onSuccess: async () => {
      toast.success("Club ban applied");
      await Promise.all([
        utils.dispute.getPlayerHistory.invalidate({ clubId, userId: player.userId }),
        utils.clubBan.listClubBans.invalidate({ clubId }),
      ]);
      setClubBanReason("");
      onClose();
    },
    onError: (error) => toast.error(error.message || "Failed to apply club ban"),
  });

  const isAnyBusy =
    warnMutation.isPending || queueBanMutation.isPending || clubBanMutation.isPending;

  const getExpiresAt = (): string | undefined => {
    if (clubBanPermanent) return undefined;
    const ms = parseDuration(clubBanDuration);
    if (!ms) return undefined;
    return new Date(Date.now() + ms).toISOString();
  };

  return (
    <Modal
      open={true}
      onClose={() => {
        if (!isAnyBusy) onClose();
      }}
      className="!max-w-lg"
    >
      <ModalHeader subtitle={`Dispute #${disputeId.slice(-8)} \u00B7 Match #${matchId.slice(-8)}`}>
        Player Actions
      </ModalHeader>
      <ModalBody className="space-y-4">
        {/* Player Header */}
        <div className="flex items-center gap-3 rounded-lg border border-surface-800/60 bg-surface-875 p-3">
          <Avatar
            src={player.avatarUrl}
            alt={player.username}
            initials={player.username.slice(0, 1)}
            size="md"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-surface-100">{player.username}</p>
            <div className="mt-0.5 flex items-center gap-2">
              <RankIcon
                rank={rank.rank as import("../../../../../components/ui/RankIcon").Rank}
                level={rank.level as import("@rivaled/shared").RankLevel | undefined}
                size="xs"
                showLevels
                levelLayout="right"
              />
              <span className="text-xs text-surface-500">ELO {player.eloAtMatch}</span>
            </div>
          </div>
          <Link
            to="/app/clubs/$slug/admin/moderation"
            params={{ slug }}
            search={{ userId: player.userId, username: player.username }}
            className="shrink-0 rounded-md border border-surface-700/60 bg-surface-800/40 px-2.5 py-1 text-xs font-semibold text-surface-200 hover:text-brand-300 hover:border-brand-500/40 transition-colors"
          >
            Full History
          </Link>
        </div>

        {/* Quick Stats */}
        {playerHistoryQuery.isLoading ? (
          <div className="grid grid-cols-4 gap-1.5">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 rounded-lg bg-surface-800/50 animate-pulse" />
            ))}
          </div>
        ) : playerHistoryQuery.error ? (
          <div className="rounded-lg border border-error-500/40 bg-error-950/30 p-2">
            <p className="text-xs text-error-300">
              Unable to load moderation stats for this player right now.
            </p>
            <div className="mt-2">
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  void playerHistoryQuery.refetch();
                }}
                disabled={playerHistoryQuery.isFetching}
              >
                {playerHistoryQuery.isFetching ? "Retrying..." : "Retry"}
              </Button>
            </div>
          </div>
        ) : summary ? (
          <div className="grid grid-cols-4 gap-1.5">
            <div className="rounded-lg border border-surface-700/50 bg-surface-875 px-2 py-1.5">
              <p className="text-2xs text-surface-500">Filed</p>
              <p className="text-sm font-semibold text-surface-100">{summary.disputesFiled}</p>
            </div>
            <div className="rounded-lg border border-surface-700/50 bg-surface-875 px-2 py-1.5">
              <p className="text-2xs text-surface-500">Named In</p>
              <p className="text-sm font-semibold text-surface-100">
                {summary.disputesAsParticipant}
              </p>
            </div>
            <div className="rounded-lg border border-surface-700/50 bg-surface-875 px-2 py-1.5">
              <p className="text-2xs text-surface-500">Warnings</p>
              <p className="text-sm font-semibold text-surface-100">{summary.totalWarnings}</p>
            </div>
            <div className="rounded-lg border border-surface-700/50 bg-surface-875 px-2 py-1.5">
              <p className="text-2xs text-surface-500">Active Bans</p>
              <p className="text-sm font-semibold text-surface-100">
                {summary.activeQueueBans + summary.activeClubBans}
              </p>
            </div>
          </div>
        ) : null}

        {/* Section 1: Warn (lowest severity) */}
        <div className="rounded-xl border border-surface-800/50 bg-surface-850/60 p-3 space-y-2.5">
          <div className="flex items-center gap-2">
            <AlertTriangle className="size-4 text-yellow-400" />
            <p className="text-xs font-bold uppercase tracking-wider text-surface-300">
              Issue Warning
            </p>
          </div>
          <Textarea
            value={warnReason}
            onChange={(e) => setWarnReason(e.target.value)}
            placeholder="Reason for warning (min 10 characters)..."
            rows={2}
            resize="none"
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-surface-500">
              {warnReason.trim().length < 10
                ? `${10 - warnReason.trim().length} more characters needed`
                : "Recorded in player history"}
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={warnReason.trim().length < 10 || isAnyBusy}
                >
                  {warnMutation.isPending ? "Issuing..." : "Issue Warning"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm Warning</AlertDialogTitle>
                  <AlertDialogDescription>
                    Issue a formal warning to <strong>{player.username}</strong>? This will be
                    permanently recorded in their moderation history.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() =>
                      warnMutation.mutate({
                        disputeId,
                        clubId,
                        userId: player.userId,
                        reason: warnReason.trim(),
                      })
                    }
                  >
                    Confirm Warning
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Section 2: Queue Ban (medium severity) */}
        <div className="rounded-xl border border-surface-800/50 bg-surface-850/60 p-3 space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Ban className="size-4 text-orange-400" />
              <p className="text-xs font-bold uppercase tracking-wider text-surface-300">
                Queue Ban
              </p>
            </div>
            {!canResolve && (
              <Badge variant="subtle" color="gray">
                Admin Only
              </Badge>
            )}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Select
              options={[
                { value: "no_show", label: "No Show" },
                { value: "declined", label: "Declined" },
                { value: "abuse", label: "Abuse" },
                { value: "other", label: "Other" },
              ]}
              value={queueBanReasonValue}
              onChange={setQueueBanReasonValue}
              size="sm"
              disabled={!canResolve}
            />
            <Textarea
              value={queueBanDetails}
              onChange={(e) => setQueueBanDetails(e.target.value)}
              placeholder="Details (optional)..."
              rows={1}
              resize="none"
              disabled={!canResolve}
            />
          </div>
          <div className="flex items-center justify-end">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={!canResolve || isAnyBusy}>
                  {queueBanMutation.isPending ? "Applying..." : "Apply Queue Ban"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm Queue Ban</AlertDialogTitle>
                  <AlertDialogDescription>
                    Apply a queue ban to <strong>{player.username}</strong> for{" "}
                    <strong>{formatBanReasonLabel(queueBanReasonValue)}</strong>? They will be
                    temporarily unable to join matchmaking queues.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() =>
                      queueBanMutation.mutate({
                        clubId,
                        userId: player.userId,
                        reason: queueBanReasonValue as "no_show" | "declined" | "abuse" | "other",
                        details: queueBanDetails.trim() || undefined,
                      })
                    }
                  >
                    Confirm Queue Ban
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Section 3: Club Ban (highest severity) */}
        <div className="rounded-xl border border-error-500/20 bg-error-500/5 p-3 space-y-2.5">
          <div className="flex items-center gap-2">
            <Ban className="size-4 text-error-400" />
            <p className="text-xs font-bold uppercase tracking-wider text-error-300">Club Ban</p>
          </div>
          <Textarea
            value={clubBanReason}
            onChange={(e) => setClubBanReason(e.target.value)}
            placeholder="Reason for club ban (min 10 characters)..."
            rows={2}
            resize="none"
          />
          <div className="flex items-center gap-3">
            <Switch
              checked={clubBanPermanent}
              onChange={() => setClubBanPermanent(!clubBanPermanent)}
              label="Permanent"
              size="sm"
            />
            {!clubBanPermanent && (
              <Select
                options={[
                  { value: "1d", label: "1 Day" },
                  { value: "3d", label: "3 Days" },
                  { value: "7d", label: "7 Days" },
                  { value: "14d", label: "14 Days" },
                  { value: "30d", label: "30 Days" },
                  { value: "90d", label: "90 Days" },
                ]}
                value={clubBanDuration}
                onChange={setClubBanDuration}
                size="sm"
              />
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-error-300/70">
              {clubBanPermanent
                ? "Permanently removed from the club."
                : `Ban expires after ${formatDurationLabel(clubBanDuration)}.`}
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={clubBanReason.trim().length < 10 || isAnyBusy}
                >
                  {clubBanMutation.isPending ? "Applying..." : "Apply Club Ban"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm Club Ban</AlertDialogTitle>
                  <AlertDialogDescription>
                    Ban <strong>{player.username}</strong> from this club
                    {clubBanPermanent
                      ? " permanently"
                      : ` for ${formatDurationLabel(clubBanDuration)}`}
                    ? This will immediately remove them from the club.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() =>
                      clubBanMutation.mutate({
                        clubId,
                        userId: player.userId,
                        reason: clubBanReason.trim(),
                        expiresAt: getExpiresAt(),
                      })
                    }
                  >
                    Confirm Club Ban
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" size="md" onClick={onClose} disabled={isAnyBusy}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function parseDuration(value: string): number | null {
  const match = value.match(/^(\d+)([dhm])$/);
  if (!match) return null;
  const num = Number.parseInt(match[1], 10);
  const unit = match[2];
  if (unit === "d") return num * 86400 * 1000;
  if (unit === "h") return num * 3600 * 1000;
  if (unit === "m") return num * 60 * 1000;
  return null;
}

function formatDurationLabel(value: string): string {
  const match = value.match(/^(\d+)([dhm])$/);
  if (!match) return value;
  const num = match[1];
  const unit = match[2];
  if (unit === "d") return `${num} day${num === "1" ? "" : "s"}`;
  if (unit === "h") return `${num} hour${num === "1" ? "" : "s"}`;
  if (unit === "m") return `${num} minute${num === "1" ? "" : "s"}`;
  return value;
}

function formatBanReasonLabel(reason: string): string {
  switch (reason) {
    case "no_show":
      return "No Show";
    case "declined":
      return "Declined";
    case "abuse":
      return "Abuse";
    case "other":
      return "Other";
    default:
      return reason;
  }
}

function PlayerQuickInfoPopover({
  slug,
  clubId,
  userId,
  userSlug,
  username,
  avatarUrl,
  eloAtMatch,
  onModerate,
}: {
  slug: string;
  clubId: string;
  userId: string;
  userSlug?: string;
  username: string;
  avatarUrl: string | null;
  eloAtMatch: number;
  onModerate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const playerHistoryQuery = trpc.dispute.getPlayerHistory.useQuery(
    { clubId, userId },
    { enabled: open && !!clubId },
  );
  const summary = playerHistoryQuery.data?.summary;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="truncate text-sm text-surface-100 hover:text-brand-300 transition-colors"
          title={`Open quick info for ${username}`}
        >
          {username}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <Avatar src={avatarUrl} alt={username} initials={username.slice(0, 1)} size="sm" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-surface-100">{username}</p>
            <p className="text-xs text-surface-500">ELO at match: {eloAtMatch}</p>
          </div>
        </div>

        {playerHistoryQuery.isLoading ? (
          <div className="grid grid-cols-3 gap-1.5">
            {[1, 2, 3].map((item) => (
              <div key={item} className="h-12 rounded-lg bg-surface-800/50 animate-pulse" />
            ))}
          </div>
        ) : playerHistoryQuery.error ? (
          <p className="text-xs text-error-300">
            Unable to load moderation stats for this player right now.
          </p>
        ) : summary ? (
          <div className="grid grid-cols-3 gap-1.5">
            <div className="rounded-lg border border-surface-700/50 bg-surface-875 px-2 py-1.5">
              <p className="text-2xs text-surface-500">Filed</p>
              <p className="text-sm font-semibold text-surface-100">{summary.disputesFiled}</p>
            </div>
            <div className="rounded-lg border border-surface-700/50 bg-surface-875 px-2 py-1.5">
              <p className="text-2xs text-surface-500">Named</p>
              <p className="text-sm font-semibold text-surface-100">
                {summary.disputesAsParticipant}
              </p>
            </div>
            <div className="rounded-lg border border-surface-700/50 bg-surface-875 px-2 py-1.5">
              <p className="text-2xs text-surface-500">Active Bans</p>
              <p className="text-sm font-semibold text-surface-100">
                {summary.activeQueueBans + summary.activeClubBans}
              </p>
            </div>
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          {onModerate && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setOpen(false);
                onModerate();
              }}
              leftIcon={<Shield className="size-3" />}
            >
              Take Action
            </Button>
          )}
          <Link
            to="/app/clubs/$slug/admin/moderation"
            params={{ slug }}
            search={{ userId, username }}
            className="inline-flex items-center rounded-md border border-surface-700/60 bg-surface-875 px-2.5 py-1 text-xs font-semibold text-surface-200 hover:text-brand-300 hover:border-brand-500/40 transition-colors"
          >
            History
          </Link>
          <Link
            to="/app/users/$slug"
            params={{ slug: userSlug ?? userId }}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-md border border-surface-700/60 bg-surface-875 px-2.5 py-1 text-xs font-semibold text-surface-200 hover:text-brand-300 hover:border-brand-500/40 transition-colors"
          >
            Profile
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function EvidenceViewButton({ evidenceId }: { evidenceId: string }) {
  const tokenQuery = trpc.dispute.getEvidenceToken.useQuery({ evidenceId });

  return (
    <a
      href={tokenQuery.data ? `${apiBaseUrl}${tokenQuery.data.url}` : undefined}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        if (!tokenQuery.data) e.preventDefault();
      }}
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors",
        tokenQuery.data
          ? "text-brand-400 hover:text-brand-300 hover:bg-surface-800/40"
          : "text-surface-500 cursor-wait",
      )}
    >
      View
      <ExternalLink className="size-3" />
    </a>
  );
}

function renderWinner(winningTeam: number | null, getTeamName: (teamIndex: number) => string) {
  if (winningTeam === null) return "Unresolved";
  return getTeamName(winningTeam);
}

function formatDisputeStatus(status: string) {
  switch (status) {
    case "under_review":
      return "Under Review";
    case "open":
      return "Open";
    case "resolved":
      return "Resolved";
    default:
      return status;
  }
}

function formatCategory(value: string) {
  switch (value) {
    case "wrong_result":
      return "Wrong Result";
    case "cheating":
      return "Cheating";
    case "abandonment":
      return "Abandon";
    case "technical":
      return "Technical";
    case "other":
      return "Other";
    default:
      return value;
  }
}

function formatDisputeResolution(resolution: string) {
  switch (resolution) {
    case "upheld":
      return "Upheld";
    case "overturned":
      return "Overturned";
    case "cancelled":
      return "Cancelled";
    default:
      return resolution;
  }
}

function formatTimelineEvent(eventType: string): string {
  switch (eventType) {
    case "created":
      return "Dispute created";
    case "claimed":
      return "Dispute claimed";
    case "unclaimed":
      return "Claim released";
    case "status_changed":
      return "Status changed";
    case "resolved_upheld":
      return "Resolved as upheld";
    case "resolved_overturned":
      return "Resolved as overturned";
    case "resolved_cancelled":
      return "Resolved as cancelled";
    case "withdrawn":
      return "Reporter withdrew dispute";
    case "note_added":
      return "Admin note added";
    case "note_edited":
      return "Admin note edited";
    case "evidence_added":
      return "Evidence added";
    case "participant_warned":
      return "Participant warned";
    case "system_auto_resolved":
      return "Auto-resolved by system";
    default:
      return eventType;
  }
}

function formatTimelineActor(username: string | null, userId: string | null): string {
  if (username) return `by ${username}`;
  if (userId) return `by ${userId.slice(-8)}`;
  return "by system";
}

function formatTimelineContext(metadata: Record<string, unknown> | null): string | null {
  if (!metadata) return null;
  const parts: string[] = [];

  const previousStatus =
    typeof metadata.previousStatus === "string" ? metadata.previousStatus : null;
  const newStatus = typeof metadata.newStatus === "string" ? metadata.newStatus : null;
  if (previousStatus && newStatus) {
    parts.push(
      `status ${formatDisputeStatus(previousStatus)} -> ${formatDisputeStatus(newStatus)}`,
    );
  }

  const previousResolution =
    typeof metadata.previousResolution === "string" ? metadata.previousResolution : null;
  const newResolution = typeof metadata.newResolution === "string" ? metadata.newResolution : null;
  if (previousResolution && newResolution) {
    parts.push(
      `resolution ${formatDisputeResolution(previousResolution)} -> ${formatDisputeResolution(newResolution)}`,
    );
  }

  const category = typeof metadata.category === "string" ? metadata.category : null;
  if (category) {
    parts.push(`category ${formatCategory(category)}`);
  }

  const matchId = typeof metadata.matchId === "string" ? metadata.matchId : null;
  if (matchId) {
    parts.push(`match #${matchId.slice(-8)}`);
  }

  const correctedWinner =
    typeof metadata.correctedWinner === "number" ? metadata.correctedWinner : null;
  if (correctedWinner !== null) {
    parts.push(`corrected winner team ${correctedWinner + 1}`);
  }

  const targetUsername =
    typeof metadata.targetUsername === "string" ? metadata.targetUsername : null;
  const targetUserId = typeof metadata.targetUserId === "string" ? metadata.targetUserId : null;
  if (targetUsername) {
    parts.push(`warned: ${targetUsername}`);
  } else if (targetUserId) {
    parts.push(`warned: #${targetUserId.slice(-8)}`);
  }

  const reason = typeof metadata.reason === "string" ? metadata.reason : null;
  if (reason) {
    parts.push(reason.replaceAll("_", " "));
  }

  return parts.length > 0 ? parts.join(" • ") : null;
}

function formatAgo(value: Date | string) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";

  const deltaSeconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (deltaSeconds < 60) return `${deltaSeconds}s ago`;
  if (deltaSeconds < 3600) return `${Math.floor(deltaSeconds / 60)}m ago`;
  if (deltaSeconds < 86400) return `${Math.floor(deltaSeconds / 3600)}h ago`;
  return `${Math.floor(deltaSeconds / 86400)}d ago`;
}

function formatSignedNumber(value: number) {
  if (!Number.isFinite(value)) return "0";
  if (value > 0) return `+${value}`;
  return String(value);
}

function formatBytes(value: number | null) {
  if (value === null || !Number.isFinite(value) || value <= 0) return "";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function evidenceMeta(kind: string, mimeType?: string | null) {
  switch (kind) {
    case "image":
      return {
        icon: ImageIcon,
        label: "Screenshot",
        bgClass: "bg-blue-500/15",
        iconClass: "text-blue-400",
      };
    case "video":
      return {
        icon: PlayCircle,
        label: "Video Clip",
        bgClass: "bg-yellow-500/15",
        iconClass: "text-yellow-400",
      };
    case "log":
      return {
        icon: FileText,
        label: mimeType === "application/json" ? "JSON Log" : "Text Log",
        bgClass: "bg-surface-700/60",
        iconClass: "text-surface-300",
      };
    case "link":
      return {
        icon: Link2,
        label: "External Link",
        bgClass: "bg-green-500/15",
        iconClass: "text-green-400",
      };
    default:
      return {
        icon: Paperclip,
        label: "Attachment",
        bgClass: "bg-surface-700/60",
        iconClass: "text-surface-300",
      };
  }
}

function formatDomain(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "Open link";
  }
}
